<template>
  <Modal v-if="visible" ref="modal" class="modal-user-createdit" :actions="actions" @action="handleAction" immediate>
    <h1>{{ $t(`users.modals.${mode}.title`) }}</h1>
    <form>
      <div class="group">
        <TextInput
          fill
          label="Vorname"
          error-message="Vorname ist ein Pflichtfeld"
          :vuelidate="$v.form.firstname"
          :value.sync="form.firstname"
        />
      </div>
      <div class="group">
        <TextInput
          fill
          label="Nachname"
          error-message="Nachname ist ein Pflichtfeld"
          :vuelidate="$v.form.lastname"
          :value.sync="form.lastname"
        />
      </div>
      <div class="group">
        <TextInput
          fill
          label="E-Mail Adresse"
          error-message="E-Mail ist ein Pflichtfeld"
          :vuelidate="$v.form.email"
          :value.sync="form.email"
        />
      </div>
      <div class="group">
        <TextInput
          fill
          label="Mobile Nummer"
          hint="Dient zum Senden des SMS-Codes beim Anmelden."
          error-message="Mobile Nummer ist ein Pflichtfeld"
          mask="nnn nnn nn nn"
          placeholder="079 123 45 67"
          :vuelidate="$v.form.phone"
          :value.sync="form.phone"
        />
      </div>
      <hr v-if="(canAssignBranch && !isVsaManager) || canAssignRoles">
      <div class="group" v-if="canAssignBranch && !isVsaManager">
        <Select
          fill
          label="SPD"
          placeholder="-- Auswählen --"
          :options="branches"
          error-message="SPD ist ein Pflichtfeld"
          :vuelidate="$v.form.branch"
          :value.sync="form.branch"
        />
      </div>
      <div class="group" v-if="canAssignRoles">
        <Select
          fill
          placeholder="-- Auswählen --"
          label="Rolle"
          :options="roles"
          error-message="Rolle ist ein Pflichtfeld"
          :vuelidate="$v.form.role"
          :value.sync="form.role"
        />
      </div>
    </form>
  </Modal>
</template>

<script>
  import { required, email, minLength, maxLength } from 'vuelidate/lib/validators';
  import Modal from '@/components/ui/Modal';
  import TextInput from '@/components/ui/inputs/TextInput';
  import TextareaInput from '@/components/ui/inputs/TextareaInput';
  import Select from '@/components/ui/inputs/Select';
  import { Store } from '@/store';
  import UserRoles from '@/objects/UserRoles';

  export default {
    name: 'ModalUserCreateEdit',
    components: {
      Select,
      TextareaInput,
      TextInput,
      Modal,
    },
    //---------------------------------------------------
    //
    //  Properties
    //
    //---------------------------------------------------
    props: {
      uuid: {
        type: String,
        default: null,
      },
    },
    //---------------------------------------------------
    //
    //  Data model
    //
    //---------------------------------------------------
    data() {
      return {
        visible: false,
        mode: this.uuid ? 'edit' : 'create',
        branches: [],
        form: {
          firstname: null,
          lastname: null,
          email: null,
          phone: null,
          extras: {
            note: null,
            title: null,
          },
          branch: null,
          role: null,
        },
      };
    },
    //---------------------------------------------------
    //
    //  Computed Properties
    //
    //---------------------------------------------------
    computed: {
      actions() {
        const i18n = this.$i18n;
        const out = [{ value: 'cancel', label: i18n.t('labels.cancel') }];
        if (this.uuid) {
          out.push({ value: 'update', label: i18n.t('labels.save') });
        } else {
          out.push({ value: 'save', label: i18n.t('labels.save') });
        }
        return out;
      },
      roles() {
        const roles = this.$i18n.t('roles');
        const keys = Object.keys(roles);
        const out = [];
        keys.forEach((key) => {
          if (Store.permissions.userCanAssignRole(key)) {
            out.push({ value: key, label: roles[key] });
          }
        });
        return out;
      },
      canAssignBranch() {
        return Store?.permissions?.branchCanCreate;
      },
      canAssignRoles() {
        const userRole = this.form?.role;
        return userRole !== UserRoles.VSA_MANAGER;
      },
      isVsaManager() {
        const userRole = this.form?.role;
        return userRole === UserRoles.VSA_MANAGER;
      },
    },
    //---------------------------------------------------
    //
    //  Watch Properties
    //
    //---------------------------------------------------
    watch: {},
    //---------------------------------------------------
    //
    //  Filter Properties
    //
    //---------------------------------------------------
    // filters: {},
    //---------------------------------------------------
    //
    //  Validation Properties
    //
    //---------------------------------------------------
    validations() {
      return {
        form: {
          firstname: { required, minLength: minLength(2) },
          lastname: { required, minLength: minLength(2) },
          email: { required, email },
          phone: {
            required,
            minLength: minLength(13),
            maxLength: maxLength(13),
          },
          ...(this.canAssignBranch ? {
            branch: { required },
          } : {}),
          ...(this.canAssignRoles ? {
            role: { required },
          } : {}),
        },
      };
    },
    //---------------------------------------------------
    //
    //  Vue Lifecycle
    //
    //---------------------------------------------------
    // beforeCreate() {},
    async created() {
      const rawBranches = await this.$sav.api.get('/branches');
      this.branches = rawBranches.map(branch => ({
        value: branch.uuid,
        label: branch.name,
      }));

      const uuid = this.uuid;
      if (uuid) {
        const user = await this.$sav.api.get(`/user/${uuid}`);
        this.form = {
          firstname: user?.firstname,
          lastname: user?.lastname,
          email: user?.email,
          phone: user?.phone,
          extras: user?.extras || { note: null, title: null },
          branch: user?.branch?.uuid || null,
          role: user?.role,
        };
        this.visible = true;
      } else {
        this.visible = true;
      }
    },
    // beforeMount() {},
    // render(h) { return h(); },
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // beforeDestroy() {},
    // destroyed() {},
    //---------------------------------------------------
    //
    //  Methods
    //
    //---------------------------------------------------
    methods: {
      //----------------------------------
      // Event Handlers
      //----------------------------------
      async handleAction(action) {
        if (['save', 'update'].includes(action)) {
          const $v = this.$v;
          $v.$touch();
          if (!$v.$invalid) {
            const uuid = this.uuid;
            const form = this.form;
            form.branch = form.branch || Store.branch.uuid;

            let response;
            if (uuid) {
              response = await this.$sav.api.patch(`/user/${uuid}`, null, form);
            } else {
              response = await this.$sav.api.post('/user', null, form);
            }
            if (response) {
              this.$emit('done');
              this.$refs.modal.close();
            }
          }
        } else {
          this.$refs.modal.close();
        }
      },
    },
  };
</script>

<style lang="scss">
@import '~scss/mixins';
@import '~scss/variables';

.modal-user-createdit {
  h1 {
    margin-bottom: 12px;
  }

  form {
    padding: 24px 0;

    .group {
      margin-bottom: 24px;
      &:last-of-type {
        margin-bottom: 0;
      }
    }

    hr {
      border: 0;
      margin: 24px 0;
      border-bottom: 1px solid $color-black-alpha-10;
    }
  }
}
</style>
